import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { useSettings } from "../../lib/settings";
import { api, formatError } from "../../lib/api";
import { Shell } from "./_shell";
import { ImagePicker, Field, Toggle, ConfirmDialog } from "./_shared";

export default function Settings() {
  const { refresh } = useSettings();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings").then(r => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <Shell active="settings"><div className="p-10">Loading…</div></Shell>;
  const upd = (k, v) => setS(prev => ({ ...prev, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const { id, updated_at, ...payload } = s;
      await api.put("/settings", payload);
      toast.success("Settings saved"); refresh();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const F = (label, k, opts = {}) => (
    <Field label={label} value={s[k]} onChange={v => upd(k, v)} testid={`set-${k}`} {...opts} />
  );
  const T = (label, k) => (
    <Toggle label={label} checked={s[k]} onChange={v => upd(k, v)} testid={`toggle-${k}`} />
  );
  return (
    <Shell active="settings">
      <div className="p-5 md:p-10 max-w-4xl">
        <div className="overline mb-2">Website content</div>
        <h1 className="font-display text-4xl md:text-6xl uppercase leading-none mb-8">Settings.</h1>

        <div className="border border-ink p-5 md:p-6 mb-6 bg-paper">
          <div className="overline mb-4">Business identity</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {F("Business name", "business_name")}
            {F("Public email", "public_email")}
            {F("Phone", "phone")}
            {F("Address", "address")}
            {F("Operating hours", "hours")}
            {F("Business Contact Email (receives all form notifications)", "business_contact_email", { placeholder: "e.g. sales@gtawholesalesupply.ca" })}
          </div>
          <p className="text-[11px] text-black/50 mt-3">The Business Contact Email is used for every Contact form, Retailer application, Wholesale enquiry and future website email. It is kept private and never shown publicly. It is separate from the admin login email.</p>
        </div>

        <div className="border border-ink p-5 md:p-6 mb-6 bg-paper">
          <div className="overline mb-4">Announcement strip (homepage)</div>
          <div className="space-y-2" data-testid="announcements-editor">
            {(s.announcements || []).map((a, i) => (
              <div key={i} className="flex gap-2">
                <input value={a} onChange={e => { const arr = [...s.announcements]; arr[i] = e.target.value; upd("announcements", arr); }} className="field" data-testid={`announcement-${i}`} />
                <button onClick={() => upd("announcements", s.announcements.filter((_, j) => j !== i))} className="btn-ghost px-3" data-testid={`announcement-del-${i}`}>×</button>
              </div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-3 items-end">
            <button onClick={() => upd("announcements", [...(s.announcements || []), "New announcement"])} className="btn-ghost" data-testid="announcement-add">+ Add announcement</button>
            <div>
              <label className="overline block mb-2">Scroll speed ({s.marquee_speed || 40})</label>
              <input type="range" min="10" max="120" value={s.marquee_speed || 40} onChange={e => upd("marquee_speed", parseInt(e.target.value))} data-testid="set-marquee_speed" />
            </div>
          </div>
        </div>

        <div className="border border-ink p-5 md:p-6 mb-6 bg-paper">
          <div className="overline mb-4">Delivery settings</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="overline block mb-2">Free delivery amount</label>
              <input type="number" step="1" value={s.free_delivery_amount ?? ""} onChange={e => upd("free_delivery_amount", parseFloat(e.target.value) || 0)} className="field" data-testid="set-free_delivery_amount" />
            </div>
            <div>
              <label className="overline block mb-2">Default low-stock threshold</label>
              <input type="number" min="0" step="1" value={s.default_low_stock_threshold ?? 10} onChange={e => upd("default_low_stock_threshold", parseInt(e.target.value) || 0)} className="field" data-testid="set-default_low_stock_threshold" />
              <div className="text-[10px] text-black/50 mt-1">Used when a product doesn't have its own threshold. Alerts appear on /admin/low-stock.</div>
            </div>
            <div>
              <label className="overline block mb-2">Currency symbol</label>
              <input value={s.currency || ""} onChange={e => upd("currency", e.target.value)} placeholder="$ / CAD$ / €" className="field" data-testid="set-currency" />
            </div>
            <div className="md:col-span-3">
              <label className="overline block mb-2">Delivery message</label>
              <textarea rows={2} value={s.delivery_info || ""} onChange={e => upd("delivery_info", e.target.value)} className="field" data-testid="set-delivery_info" />
            </div>
          </div>
        </div>

        <div className="border border-ink p-5 md:p-6 mb-6 bg-paper">
          <div className="overline mb-4">Homepage</div>
          <div className="grid grid-cols-1 gap-4">
            {F("Homepage headline", "homepage_headline")}
            {F("Homepage sub-headline", "homepage_subheadline", { area: true })}
            <div>
              <label className="overline block mb-2">Homepage hero image</label>
              <ImagePicker value={s.homepage_hero_image} onChange={v => upd("homepage_hero_image", v)} testid="set-hero" label="hero image" />
            </div>
            {F("Footer text", "footer_text", { area: true })}
          </div>
        </div>

        <div className="border border-ink p-5 md:p-6 mb-6 bg-paper">
          <div className="overline mb-4">Social links</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {F("Facebook URL", "facebook_url", { placeholder: "https://…" })}
            {F("Instagram URL", "instagram_url", { placeholder: "https://…" })}
            {F("LinkedIn URL", "linkedin_url", { placeholder: "https://…" })}
          </div>
        </div>

        <div className="border border-ink p-5 md:p-6 mb-6 bg-paper">
          <div className="overline mb-4">Public toggles</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {T("Show phone publicly", "show_phone")}
            {T("Show email publicly", "show_email")}
            {T("Enquiry form enabled", "enquiry_form_enabled")}
            {T("Retailer login enabled", "retailer_login_enabled")}
            {T("WhatsApp button enabled", "whatsapp_enabled")}
            {T("Online ordering enabled (retailers)", "ordering_enabled")}
          </div>
          {s.whatsapp_enabled && <div className="mt-4">{F("WhatsApp number", "whatsapp_number")}</div>}
        </div>

        <div className="border border-soda p-5 md:p-6 mb-6 bg-paper" data-testid="danger-zone">
          <div className="overline text-soda mb-2">Danger zone</div>
          <div className="font-display text-xl uppercase mb-2">Reset demo data</div>
          <p className="text-sm text-black/70 mb-4">
            Deletes <b>all products, brands, retailers, enquiries, contact messages, orders and reviews</b>.
            Keeps categories, your admin account, website settings and uploaded images.
            Use this once when going live to remove seeded/test entries.
          </p>
          <DangerResetButton />
        </div>

        <div className="flex gap-3 sticky bottom-0 bg-paper py-4 border-t border-ink">
          <button disabled={busy} onClick={save} className="btn-primary" data-testid="settings-save">{busy ? "Saving…" : "Save changes"}</button>
        </div>
      </div>
    </Shell>
  );
}

// Standalone button so its own local state (confirming, busy) doesn't affect the parent form.
function DangerResetButton() {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const doReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-demo-data");
      const d = data.deleted;
      toast.success(`Reset done — removed ${d.products} products, ${d.brands} brands, ${d.retailers} retailers, ${d.enquiries + d.inquiries} messages`);
      setConfirming(false);
      setTimeout(() => window.location.reload(), 800);
    } catch (e) { toast.error(formatError(e.response?.data?.detail) || "Reset failed"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <button
        onClick={() => setConfirming(true)}
        className="btn-primary text-xs"
        style={{ background: "#E63946", borderColor: "#E63946" }}
        data-testid="reset-demo-btn"
      >
        Clear demo data →
      </button>
      <ConfirmDialog
        open={confirming}
        title="Wipe demo data?"
        body={"This permanently deletes ALL products, brands, retailers, enquiries, contact messages, orders and reviews.\n\nCategories, your admin login, website settings and uploaded images are kept.\n\nThis cannot be undone."}
        confirmLabel="Yes, wipe everything" danger busy={busy}
        onConfirm={doReset} onCancel={() => setConfirming(false)}
        testid="reset-demo-dialog"
      />
    </>
  );
}
